import {createStyles, Group, Loader, Stack, Stepper, Text} from '@mantine/core';
import React, {useReducer, useMemo, useCallback} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import type {RunModalProps} from 'ui-library/runModal';

import ColumnMappingPane from './ColumnMappingPane';
import ConfigurePane from './ConfigurePane';
import FileUploadPane from './FileUploadPane';
import FinalizeImportPane from './FinalizeImportPane';
import moleculeImportReducer, {INITIAL_STATE, ModalStep} from './moleculeImportReducer';
import useSubmitImport from './useSubmitImport';

const useStyles = createStyles(() => ({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  stepContent: {
    flex: 1,
    overflow: 'auto',
  },
}));

const STEPPER_STEPS: Array<{key: ModalStep; label: string}> = [
  {key: 'CONFIGURE', label: 'Select Schema'},
  {key: 'FILE_UPLOAD', label: 'Upload File'},
  {key: 'MAP_COLUMNS', label: 'Map Columns'},
  {key: 'FINALIZE_IMPORT', label: 'Finalize Import'},
];

interface ModalFooterProps {
  currentStepIndex: number;
  step: ModalStep;
  isNextDisabled: boolean;
  isImportDisabled: boolean;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  onImport: () => void;
}

function ModalFooter({
  currentStepIndex,
  step,
  isNextDisabled,
  isImportDisabled,
  onBack,
  onNext,
  onCancel,
  onImport,
}: ModalFooterProps): JSX.Element {
  if (step === 'SUBMITTING') {
    return <Group position="apart" />;
  }

  return (
    <Group position="apart">
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        onClick={currentStepIndex === 0 ? onCancel : onBack}
      >
        {currentStepIndex === 0 ? 'Cancel' : 'Back'}
      </Button>
      {step === 'FINALIZE_IMPORT' ? (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          disabled={isImportDisabled}
          onClick={onImport}
        >
          Import
        </Button>
      ) : (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          onClick={onNext}
          disabled={isNextDisabled}
        >
          Next
        </Button>
      )}
    </Group>
  );
}

interface MoleculeImportModalProps extends RunModalProps<void> {
  initialFolderId?: string | null;
}

export default function MoleculeImportModal({close, initialFolderId}: MoleculeImportModalProps): JSX.Element {
  const {classes} = useStyles();
  const [state, dispatch] = useReducer(moleculeImportReducer, {
    ...INITIAL_STATE,
    initialFolderId: initialFolderId ?? null,
  });

  const {handleSubmit, isSubmitting} = useSubmitImport(state, dispatch, close);

  const currentStepIndex = useMemo(() => {
    const index = STEPPER_STEPS.findIndex((s) => s.key === state.step);
    return index === -1 ? STEPPER_STEPS.length : index;
  }, [state.step]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex - 1].key}});
    }
  }, [currentStepIndex]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPPER_STEPS.length - 1) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex + 1].key}});
    }
  }, [currentStepIndex]);

  const isNextDisabled = useMemo(() => {
    switch (state.step) {
      case 'CONFIGURE':
        return !state.schemaId;
      case 'FILE_UPLOAD':
        return state.headers == null;
      case 'MAP_COLUMNS':
        return !Object.values(state.columnMappings).some((v) => v != null && v !== '');
      case 'FINALIZE_IMPORT':
        return false;
      default:
        return true;
    }
  }, [state.step, state.schemaId, state.headers, state.columnMappings]);

  const isImportDisabled = useMemo(
    () =>
      isSubmitting ||
      !state.schemaId ||
      Object.values(state.columnMappings).filter((v) => v !== '').length === 0 ||
      state.registrationConfig == null ||
      !state.registrationConfig.registryId ||
      !state.registrationConfig.folderId ||
      !state.registrationConfig.namingStrategy,
    [isSubmitting, state.schemaId, state.columnMappings, state.registrationConfig]
  );

  const renderStepContent = (): JSX.Element => {
    switch (state.step) {
      case 'CONFIGURE':
        return <ConfigurePane state={state} dispatch={dispatch} />;
      case 'FILE_UPLOAD':
        return <FileUploadPane state={state} dispatch={dispatch} />;
      case 'MAP_COLUMNS':
        return <ColumnMappingPane state={state} dispatch={dispatch} />;
      case 'FINALIZE_IMPORT':
        return <FinalizeImportPane state={state} dispatch={dispatch} />;
      case 'SUBMITTING':
        return (
          <Stack align="center" spacing="md" py="xl">
            <Loader size="lg" />
            <Text>Validating and processing...</Text>
          </Stack>
        );
      case 'DONE':
        return <Text>Done!</Text>;
      default:
        return <Text>Unknown step</Text>;
    }
  };

  return (
    <Stack spacing="lg" className={classes.container}>
      <Stepper active={currentStepIndex} allowNextStepsSelect={false} breakpoint="sm">
        {STEPPER_STEPS.map((step) => (
          <Stepper.Step key={step.key} label={step.label} />
        ))}
      </Stepper>

      <div className={classes.stepContent}>{renderStepContent()}</div>

      <ModalFooter
        currentStepIndex={currentStepIndex}
        step={state.step}
        isNextDisabled={isNextDisabled}
        isImportDisabled={isImportDisabled}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={() => close()}
        onImport={handleSubmit}
      />
    </Stack>
  );
}
